import React, { useState, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
  TextField,
  Chip,
  InputAdornment,
  IconButton,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { useNotify, useDataProvider, useRefresh } from "react-admin";
import * as XLSX from "xlsx";
import { baseUrl } from "../../customDataProvider";
import { useTenant } from "../../contexts/TenantContext";

interface UserRow {
  "First Name": string;
  "Last Name": string;
  "Email": string;
  "Phone"?: string;
  "Drivers Licence"?: string;
  "DOB"?: string;
  "Role"?: string;
}

interface BulkUserUploadDialogProps {
  open: boolean;
  onClose: () => void;
}

export const BulkUserUploadDialog = ({
  open,
  onClose,
}: BulkUserUploadDialogProps) => {
  const [activeStep, setActiveStep] = useState(0);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [userData, setUserData] = useState<UserRow[]>([]);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  const notify = useNotify();
  const dataProvider = useDataProvider();
  const refresh = useRefresh();
  
  // Get tenant from context (component should be within TenantProvider)
  const tenantContext = useTenant();
  const currentTenant = tenantContext?.currentTenant || null;

  const steps = ["Upload File", "Set Password", "Review Data", "Confirm"];

  // Function to normalize column names for matching
  const normalizeColumnName = (name: string): string => {
    return name.toLowerCase().replace(/[\s_-]/g, '');
  };

  // Create a mapping of normalized names to expected names
  const columnMapping: { [key: string]: keyof UserRow } = {
    [normalizeColumnName("First Name")]: "First Name",
    [normalizeColumnName("FirstName")]: "First Name",
    [normalizeColumnName("Last Name")]: "Last Name",
    [normalizeColumnName("LastName")]: "Last Name",
    [normalizeColumnName("Email")]: "Email",
    [normalizeColumnName("Email Address")]: "Email",
    [normalizeColumnName("Phone")]: "Phone",
    [normalizeColumnName("Phone Number")]: "Phone",
    [normalizeColumnName("Drivers Licence")]: "Drivers Licence",
    [normalizeColumnName("Driver Licence")]: "Drivers Licence",
    [normalizeColumnName("drivers_licence")]: "Drivers Licence",
    [normalizeColumnName("License Number")]: "Drivers Licence",
    [normalizeColumnName("DOB")]: "DOB",
    [normalizeColumnName("Date of Birth")]: "DOB",
    [normalizeColumnName("date_of_birth")]: "DOB",
    [normalizeColumnName("Birthday")]: "DOB",
    [normalizeColumnName("Role")]: "Role",
  };

  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setSelectedFile(file);
    setErrors([]);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: "array" });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const rawData = XLSX.utils.sheet_to_json(worksheet);

        // Normalize the data by mapping column names
        const normalizedData: UserRow[] = rawData.map((row: any) => {
          const normalizedRow: any = {};

          Object.keys(row).forEach(originalKey => {
            const normalizedKey = normalizeColumnName(originalKey);
            const mappedKey = columnMapping[normalizedKey];
            if (mappedKey) {
              normalizedRow[mappedKey] = row[originalKey];
            }
          });

          return normalizedRow as UserRow;
        });

        // Validate data structure
        const validationErrors: string[] = [];
        const requiredColumns = ["First Name", "Last Name", "Email"];

        if (normalizedData.length === 0) {
          validationErrors.push("File is empty or has no data rows");
        } else {
          // Check for required fields in each row
          normalizedData.forEach((row: UserRow, index: number) => {
            if (!row["First Name"]) {
              validationErrors.push(`Missing first name in row ${index + 2}`);
            }

            if (!row["Last Name"]) {
              validationErrors.push(`Missing last name in row ${index + 2}`);
            }

            if (!row.Email) {
              validationErrors.push(`Missing email in row ${index + 2}`);
            } else {
              // Basic email validation
              const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
              if (!emailRegex.test(row.Email)) {
                validationErrors.push(`Invalid email format "${row.Email}" in row ${index + 2}`);
              }
            }
          });
        }

        if (validationErrors.length > 0) {
          setErrors(validationErrors);
        } else {
          setUserData(normalizedData);
          setActiveStep(1);
        }
      } catch (error) {
        setErrors(["Failed to parse Excel file. Please ensure it's a valid .xlsx file."]);
      }
    };
    reader.readAsArrayBuffer(file);
  }, []);

  const handleCreateUsers = async () => {
    if (!selectedFile || !password) {
      notify('Please ensure file and password are set', { type: 'error' });
      return;
    }

    setLoading(true);

    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('password', password);

      // Use direct fetch to call the backend API
      const accessToken = localStorage.getItem('accessToken');
      
      // Get tenant code - prefer context, then localStorage
      let tenantCode: string | null = null;
      const tenantsEnabled = import.meta.env.VITE_TENANTS_ENABLED === 'true';
      
      if (tenantsEnabled) {
        // First try to get from tenant context (most reliable)
        if (currentTenant && currentTenant.code) {
          tenantCode = currentTenant.code;
        } else {
          // Fallback to localStorage (same as customDataProvider)
          const currentTenantJson = localStorage.getItem('currentTenant');
          if (currentTenantJson) {
            try {
              const storedTenant = JSON.parse(currentTenantJson);
              if (storedTenant && storedTenant.code) {
                tenantCode = storedTenant.code;
              }
            } catch (e) {
              console.error('Error parsing current tenant from localStorage:', e);
            }
          }
        }
      } else {
        // If multi-tenancy is disabled, we still need a tenant code
        // But don't use 'default' - require it to be explicitly set
        const currentTenantJson = localStorage.getItem('currentTenant');
        if (currentTenantJson) {
          try {
            const storedTenant = JSON.parse(currentTenantJson);
            if (storedTenant && storedTenant.code) {
              tenantCode = storedTenant.code;
            }
          } catch (e) {
            console.error('Error parsing current tenant from localStorage:', e);
          }
        }
      }
      
      // Validate tenant code is available
      if (!tenantCode) {
        const errorMsg = 'Tenant code is required but not found. Please ensure you are logged in and a tenant is selected.';
        notify(errorMsg, { type: 'error', autoHideDuration: 10000 });
        console.error('Tenant code missing:', {
          tenantsEnabled,
          currentTenantFromContext: currentTenant,
          currentTenantFromStorage: localStorage.getItem('currentTenant'),
        });
        setLoading(false);
        return;
      }
      
      // Log tenant info for debugging
      console.log('Tenants enabled:', tenantsEnabled);
      console.log('Current tenant from context:', currentTenant);
      console.log('Current tenant from localStorage:', localStorage.getItem('currentTenant'));
      console.log('Resolved tenant code:', tenantCode);

      // Don't set Content-Type header - browser will set it automatically with boundary for FormData
      const headers: HeadersInit = {
        'Authorization': `Bearer ${accessToken}`,
        'X-Tenant-Code': tenantCode,
      };

      const uploadUrl = `${baseUrl}/users/bulk/upload`;
      console.log('Uploading to:', uploadUrl);
      console.log('Base URL:', baseUrl);
      console.log('Tenant Code:', tenantCode);
      console.log('File:', selectedFile?.name, selectedFile?.size, 'bytes');
      console.log('Users to import:', userData.length);

      const response = await fetch(uploadUrl, {
        method: 'POST',
        headers: headers,
        body: formData,
      });

      console.log('Response status:', response.status, response.statusText);

      if (!response.ok) {
        let errorText = '';
        try {
          const errorData = await response.json();
          errorText = errorData.message || errorData.error || JSON.stringify(errorData);
        } catch {
          errorText = await response.text() || `HTTP ${response.status}: ${response.statusText}`;
        }
        console.error('Upload error:', errorText);
        throw new Error(errorText);
      }

      const results = await response.json();

      console.log("Bulk user creation response:", results);

      // Handle results
      const successfulCount = results?.successful?.length || 0;
      const failedCount = results?.failed?.length || 0;
      const totalProcessed = successfulCount + failedCount;

      if (successfulCount > 0 && failedCount === 0) {
        // All succeeded
        notify(`Successfully created ${successfulCount} user${successfulCount > 1 ? 's' : ''} with driver profiles`, { 
          type: "success",
          autoHideDuration: 5000,
        });
        // Refresh the user list
        refresh();
        // Close dialog and reset after a short delay
        setTimeout(() => {
          onClose();
          handleReset();
        }, 1000);
      } else if (successfulCount > 0 && failedCount > 0) {
        // Partial success
        notify(
          `Created ${successfulCount} user${successfulCount > 1 ? 's' : ''} successfully, but ${failedCount} failed. Check console for details.`,
          { 
            type: "warning",
            autoHideDuration: 7000,
          }
        );
        console.error("User creation failures:", results.failed);
        // Refresh the user list to show successful ones
        refresh();
        // Keep dialog open so user can see what happened
        // User can manually close if they want
      } else if (failedCount > 0) {
        // All failed
        notify(
          `Failed to create ${failedCount} user${failedCount > 1 ? 's' : ''}. Check console for details.`,
          { 
            type: "error",
            autoHideDuration: 7000,
          }
        );
        console.error("User creation failures:", results.failed);
        // Don't close dialog - let user see the error and try again
      } else {
        // Unexpected response format
        notify("Bulk user creation completed, but response format was unexpected", { 
          type: "warning",
          autoHideDuration: 5000,
        });
        console.log("Unexpected response format:", results);
      }
    } catch (error: any) {
      const errorMessage = error?.body?.message || error?.message || "Unknown error";
      notify(`Bulk user creation failed: ${errorMessage}`, { 
        type: "error",
        autoHideDuration: 7000,
      });
      console.error("Bulk user creation error:", error);
      // Don't close dialog on error - let user see the error
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setActiveStep(0);
    setSelectedFile(null);
    setUserData([]);
    setPassword("");
    setShowPassword(false);
    setErrors([]);
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
      handleReset();
    }
  };

  const handleDownloadTemplate = () => {
    const templateData = [
      {
        "First Name": "John",
        "Last Name": "Doe",
        "Email": "john.doe@example.com",
        "Phone": "+1-555-0123",
        "Drivers Licence": "D1234567",
        "DOB": "1990-01-15",
        "Role": "admin"
      },
      {
        "First Name": "Jane",
        "Last Name": "Smith",
        "Email": "jane.smith@example.com",
        "Phone": "+1-555-0124",
        "Drivers Licence": "D7654321",
        "DOB": "1985-06-20",
        "Role": "user"
      },
      {
        "First Name": "Bob",
        "Last Name": "Johnson",
        "Email": "bob.johnson@example.com",
        "Phone": "",
        "Drivers Licence": "",
        "DOB": "",
        "Role": ""
      }
    ];

    const worksheet = XLSX.utils.json_to_sheet(templateData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Users");
    XLSX.writeFile(workbook, "user_upload_template.xlsx");
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "-";
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Typography variant="body1" gutterBottom>
              Upload an Excel file (.xlsx) with user data. Column names are flexible - spaces, underscores, dashes, and capitalization don't matter.
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, mt: 2 }}>
              <strong>Required columns:</strong>
            </Typography>
            <ul>
              <li><strong>First Name</strong> (User's first name)</li>
              <li><strong>Last Name</strong> (User's last name)</li>
              <li><strong>Email</strong> (Valid email address)</li>
            </ul>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Optional columns:</strong>
            </Typography>
            <ul>
              <li><strong>Phone</strong> (Phone number)</li>
              <li><strong>Drivers Licence</strong> (Driver's licence number)</li>
              <li><strong>DOB</strong> (Date of birth in YYYY-MM-DD format)</li>
              <li><strong>Role</strong> (Set to "admin" for admin users, leave empty or any other value for regular users)</li>
            </ul>

            <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
              <Typography variant="body2">
                Each user will be created with an empty driver profile. You'll set a common password for all users in the next step.
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Note:</strong> No welcome emails will be sent to users during bulk import.
              </Typography>
            </Alert>

            {errors.length > 0 && (
              <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
                <Typography variant="subtitle2">Please fix the following errors:</Typography>
                <ul style={{ margin: 0, paddingLeft: "20px" }}>
                  {errors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </Alert>
            )}

            <Box sx={{ mt: 3 }}>
              <Button
                variant="outlined"
                onClick={handleDownloadTemplate}
                sx={{ mb: 2, mr: 2 }}
                size="small"
              >
                Download Template
              </Button>
              <input
                accept=".xlsx,.xls"
                style={{ display: "none" }}
                id="upload-file"
                type="file"
                onChange={handleFileSelect}
              />
              <label htmlFor="upload-file">
                <Button variant="outlined" component="span" fullWidth size="large">
                  Choose Excel File
                </Button>
              </label>
              {selectedFile && (
                <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
                  Selected: {selectedFile.name}
                </Typography>
              )}
            </Box>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Set Password for All Users
            </Typography>
            <Typography variant="body2" sx={{ mb: 3, color: "text.secondary" }}>
              All {userData.length} users will be created with the same password. They can change it after their first login.
            </Typography>
            <TextField
              fullWidth
              type={showPassword ? "text" : "password"}
              label="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password for all users"
              helperText="This password will be set for all imported users"
              sx={{ mb: 2 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Alert severity="warning">
              <Typography variant="body2">
                Make sure to securely share this password with the users after import.
              </Typography>
            </Alert>
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Review {userData.length} users to be created:
            </Typography>

            <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>First Name</TableCell>
                    <TableCell>Last Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell>Driver Licence</TableCell>
                    <TableCell>DOB</TableCell>
                    <TableCell>Role</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {userData.map((user, index) => (
                    <TableRow key={index}>
                      <TableCell>{user["First Name"]}</TableCell>
                      <TableCell>{user["Last Name"]}</TableCell>
                      <TableCell>{user.Email}</TableCell>
                      <TableCell>{user.Phone || "-"}</TableCell>
                      <TableCell>{user["Drivers Licence"] || "-"}</TableCell>
                      <TableCell>{formatDate(user.DOB)}</TableCell>
                      <TableCell>
                        {user.Role && user.Role.toLowerCase().trim() === 'admin' ? (
                          <Chip label="Admin" color="primary" size="small" />
                        ) : (
                          <Chip label="User" size="small" />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        );

      case 3:
        return (
          <Box>
            {loading ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <LinearProgress sx={{ mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Creating users...
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Please wait while we create {userData.length} user{userData.length > 1 ? 's' : ''} and driver profiles.
                </Typography>
              </Box>
            ) : (
              <>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  You are about to create {userData.length} users with driver profiles. This action cannot be undone.
                </Alert>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Summary:
                </Typography>
                <ul>
                  <li><strong>Total users:</strong> {userData.length}</li>
                  <li><strong>Password set:</strong> {password ? "Yes (same for all users)" : "Not set"}</li>
                  <li><strong>Driver profiles:</strong> Empty profiles will be created for all users</li>
                  <li><strong>Email verification:</strong> Automatically verified</li>
                  <li><strong>User validation:</strong> Automatically validated</li>
                  <li><strong>Welcome emails:</strong> Not sent (users can log in immediately)</li>
                </ul>
                <Typography variant="body1" sx={{ mt: 2 }}>
                  Click "Create Users" to proceed with bulk user creation.
                </Typography>
              </>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={loading ? undefined : handleClose} 
      maxWidth="lg" 
      fullWidth
      disableEscapeKeyDown={loading}
    >
      <DialogTitle>
        Bulk User Upload
        {loading && <LinearProgress sx={{ mt: 1 }} />}
      </DialogTitle>

      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {renderStepContent()}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          {loading ? 'Processing...' : 'Cancel'}
        </Button>

        {activeStep > 0 && !loading && (
          <Button
            onClick={() => setActiveStep(activeStep - 1)}
            disabled={loading}
          >
            Back
          </Button>
        )}

        {activeStep === 1 && !loading && (
          <Button
            onClick={() => setActiveStep(2)}
            variant="contained"
            disabled={!password || password.length < 6}
          >
            Next
          </Button>
        )}

        {activeStep === 2 && !loading && (
          <Button
            onClick={() => setActiveStep(3)}
            variant="contained"
            disabled={userData.length === 0}
          >
            Next
          </Button>
        )}

        {activeStep === 3 && (
          <Button
            onClick={handleCreateUsers}
            variant="contained"
            color="primary"
            disabled={loading || !password || password.length < 6 || userData.length === 0}
          >
            {loading ? 'Creating...' : 'Create Users'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
